import { useEffect } from 'react';
import { KeyIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchAssignments } from '../store/assignmentsSlice';
import { fetchInventory } from '../store/keyInventorySlice';
import { fetchKeyTypes } from '../store/keyTypesSlice';
import { fetchLocations } from '../store/locationsSlice';
import { fetchPeople } from '../store/peopleSlice';
import Card from '../components/Card';
import { authApi } from '../services/api';
import { KeyInventoryItem } from '../types';

export default function MyKeys() {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.users.currentUser);
  const assignments = useAppSelector((state) => state.assignments.assignments);
  const keyInventory = useAppSelector((state) => state.keyInventory.inventory);
  const keyTypes = useAppSelector((state) => state.keyTypes.keyTypes);
  const locations = useAppSelector((state) => state.locations.locations);
  const people = useAppSelector((state) => state.people.people);
  const loading = useAppSelector((state) => 
    state.assignments.loading || state.keyInventory.loading || state.keyTypes.loading || state.locations.loading || state.people.loading
  );

  useEffect(() => {
    dispatch(fetchAssignments());
    dispatch(fetchInventory());
    dispatch(fetchKeyTypes());
    dispatch(fetchLocations());
    dispatch(fetchPeople());
  }, [dispatch]);

  // Find the Person record that matches the current user's email
  const matchingPerson = people.find(
    (p) => p.email && currentUser?.email && 
           p.email.toLowerCase() === currentUser.email.toLowerCase()
  );

  // Get assignments for the matching person
  const myAssignments = assignments.filter(
    (a) => a.status === 'active' && matchingPerson && a.personId === matchingPerson.id
  );

  const getKeyInfo = (keyInventoryItemId: string) => {
    const inventory = keyInventory.find((k: KeyInventoryItem) => k.id === keyInventoryItemId);
    if (!inventory) return null;

    const keyType = keyTypes.find((kt) => kt.id === inventory.keyTypeId);
    const location = locations.find((loc) => loc.id === inventory.locationId);

    return {
      keyTypeName: keyType?.name || 'Unknown Key',
      locationName: location?.name || 'Unknown Location',
    };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Header */}
      <header className="bg-white border-b border-secondary-200">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center mr-3">
                <KeyIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-secondary-900">KeyKeeper</h1>
                <p className="text-sm text-secondary-500">My Assigned Keys</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-secondary-900">{currentUser?.name}</p>
                <p className="text-xs text-secondary-500">{currentUser?.email}</p>
              </div>
              {!import.meta.env.DEV && (
                <a
                  href={authApi.getLogoutUrl()}
                  className="flex items-center px-3 py-2 text-sm font-medium text-secondary-600 rounded-lg hover:bg-secondary-100 transition-colors"
                  title="Sign Out"
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5" />
                  <span className="ml-2 hidden sm:inline">Sign Out</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-secondary-600">Loading your keys...</span>
          </div>
        ) : !matchingPerson ? (
          <Card>
            <div className="py-12 text-center">
              <KeyIcon className="w-12 h-12 mx-auto text-secondary-400" />
              <h3 className="mt-4 text-lg font-medium text-secondary-900">Account Not Linked</h3>
              <p className="mt-2 text-secondary-600">
                Your user account is not linked to a person record in the system.
              </p>
              <p className="mt-1 text-sm text-secondary-500">
                Contact your administrator to have your account linked so you can see assigned keys.
              </p>
            </div>
          </Card>
        ) : myAssignments.length === 0 ? (
          <Card>
            <div className="py-12 text-center">
              <KeyIcon className="w-12 h-12 mx-auto text-secondary-400" />
              <h3 className="mt-4 text-lg font-medium text-secondary-900">No Keys Assigned</h3>
              <p className="mt-2 text-secondary-600">
                You don't have any keys currently assigned to you.
              </p>
              <p className="mt-1 text-sm text-secondary-500">
                Contact your administrator if you believe this is an error.
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-secondary-900">
                Your Assigned Keys ({myAssignments.length})
              </h2>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {myAssignments.map((assignment) => {
                const keyInfo = getKeyInfo(assignment.keyInventoryItemId);
                if (!keyInfo) return null;

                return (
                  <Card key={assignment.id}>
                    <div className="flex items-start">
                      <div className="flex-shrink-0 p-2 bg-primary-100 rounded-lg">
                        <KeyIcon className="w-6 h-6 text-primary-600" />
                      </div>
                      <div className="ml-4 flex-1">
                        <h3 className="text-lg font-semibold text-secondary-900">
                          {keyInfo.keyTypeName}
                        </h3>
                        <p className="text-sm text-secondary-600">
                          {keyInfo.locationName}
                        </p>
                        <div className="mt-2 flex items-center gap-4 text-xs text-secondary-500">
                          <span>Qty: {assignment.quantity}</span>
                          <span>Assigned: {formatDate(assignment.assignedAt)}</span>
                        </div>
                        {assignment.notes && (
                          <p className="mt-2 text-sm text-secondary-600 bg-secondary-50 p-2 rounded">
                            {assignment.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            <Card>
              <div className="text-center py-4">
                <p className="text-sm text-secondary-600">
                  Need to return a key? Contact your administrator.
                </p>
              </div>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
